import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, BookOpen, Dices, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CUISINES, cuisineOf } from "@/lib/cuisines";
import { adjustedTotalTime } from "@/lib/time";

const FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "mine", label: "Mes recettes" },
  { id: "shared", label: "Partagées" },
  { id: "fav", label: "Favoris" },
];

const TIME_BUCKETS = [
  { id: "b25", label: "≤ 25 min", test: (t) => t > 0 && t <= 25 },
  { id: "b45", label: "25–45 min", test: (t) => t > 25 && t <= 45 },
  { id: "b60", label: "45 min – 1h", test: (t) => t > 45 && t <= 60 },
  { id: "b60p", label: "+ 1h", test: (t) => t > 60 },
];

export default function Recipes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [cuisineFilter, setCuisineFilter] = useState("all");
  const [timeBucket, setTimeBucket] = useState("all");

  const load = () => { api.get("/recipes").then((r) => setRecipes(r.data)); };
  useEffect(() => { load(); }, []);

  const toggleFav = async (recipe) => {
    await api.post(`/recipes/${recipe.id}/favorite`);
    load();
  };

  const surprise = () => {
    if (filtered.length === 0) {
      toast.error("Aucune recette dans cette sélection");
      return;
    }
    const r = filtered[Math.floor(Math.random() * filtered.length)];
    const where = cuisineFilter !== "all" ? ` (${(cuisineOf(cuisineFilter) || {}).label || ""})` : "";
    toast.success(`Ce sera : ${r.title}${where} ✨`);
    navigate(`/recipes/${r.id}`);
  };

  const surpriseQuick = () => {
    const quick = filtered.filter((r) => {
      const t = adjustedTotalTime(r, user?.household_size);
      return t > 0 && t <= 30;
    });
    if (quick.length === 0) {
      toast.error("Aucun plat de ≤ 30 min dans cette sélection");
      return;
    }
    const r = quick[Math.floor(Math.random() * quick.length)];
    toast.success(`Rapide : ${r.title} ✨`);
    navigate(`/recipes/${r.id}`);
  };

  // Types de cuisine réellement présents dans la bibliothèque (hors "autre"), ordre stable
  const availableCuisines = Object.keys(CUISINES).filter(
    (id) => id !== "autre" && recipes.some((r) => r.cuisine === id)
  );

  // Tranches de temps réellement présentes dans la bibliothèque (temps ajusté au foyer)
  const availableBuckets = TIME_BUCKETS.filter((b) =>
    recipes.some((r) => b.test(adjustedTotalTime(r, user?.household_size)))
  );

  let filtered = recipes.filter((r) => {
    if (filter === "mine" && r.created_by !== user?.user_id) return false;
    if (filter === "shared" && r.visibility !== "shared") return false;
    if (filter === "fav" && !r.is_favorite) return false;
    if (cuisineFilter !== "all" && r.cuisine !== cuisineFilter) return false;
    if (timeBucket !== "all") {
      const bucket = TIME_BUCKETS.find((b) => b.id === timeBucket);
      if (bucket && !bucket.test(adjustedTotalTime(r, user?.household_size))) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      const inTitle = r.title.toLowerCase().includes(s);
      const inIng = (r.ingredients || []).some((i) => (i.normalized_name + i.original_name).toLowerCase().includes(s));
      return inTitle || inIng;
    }
    return true;
  });

  // Tri "les plus rapides d'abord" dès qu'une tranche de temps est sélectionnée
  if (timeBucket !== "all") {
    filtered = [...filtered].sort(
      (a, b) => adjustedTotalTime(a, user?.household_size) - adjustedTotalTime(b, user?.household_size)
    );
  }

  return (
    <div className="px-5 pt-8" data-testid="recipes-page">
      <header className="flex items-center justify-between at-fade-up">
        <h1 className="font-display text-3xl font-bold">Mes recettes</h1>
        <button data-testid="add-recipe-nav" onClick={() => navigate("/add")}
          className="flex h-10 w-10 items-center justify-center rounded-full text-white at-press at-shadow"
          style={{ background: "var(--at-brand)" }}><Plus size={22} /></button>
      </header>

      <div className="relative mt-4 at-fade-up" style={{ animationDelay: "50ms" }}>
        <Search size={18} className="absolute left-4 top-3.5" style={{ color: "var(--at-muted)" }} />
        <Input data-testid="recipe-search-input" placeholder="Rechercher un plat, un ingrédient..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12 rounded-2xl border-black/10 bg-white pl-11 text-base" />
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1 at-fade-up" style={{ animationDelay: "90ms" }}>
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium at-press ${filter === f.id ? "text-white" : ""}`}
            style={filter === f.id ? { background: "var(--at-text)", borderColor: "var(--at-text)" } : { borderColor: "var(--at-border)", color: "var(--at-text-2)" }}>
            {f.label}
          </button>
        ))}
      </div>

      {availableBuckets.length > 0 && (
        <div className="mt-2 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 at-fade-up" style={{ animationDelay: "100ms" }} data-testid="time-filters">
          <span className="flex shrink-0 items-center gap-1 pr-0.5 text-xs font-semibold" style={{ color: "var(--at-muted)" }}>
            <Zap size={13} /> Temps
          </span>
          <button data-testid="time-filter-all" onClick={() => setTimeBucket("all")}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium at-press ${timeBucket === "all" ? "text-white" : ""}`}
            style={timeBucket === "all" ? { background: "var(--at-honey)", borderColor: "var(--at-honey)" } : { borderColor: "var(--at-border)", color: "var(--at-text-2)" }}>
            Tous
          </button>
          {availableBuckets.map((b) => {
            const active = timeBucket === b.id;
            return (
              <button key={b.id} data-testid={`time-filter-${b.id}`} onClick={() => setTimeBucket(active ? "all" : b.id)}
                className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-semibold at-press ${active ? "text-white" : ""}`}
                style={active ? { background: "var(--at-honey)", borderColor: "var(--at-honey)" } : { borderColor: "var(--at-border)", color: "var(--at-text-2)" }}>
                {b.label}
              </button>
            );
          })}
        </div>
      )}

      {availableCuisines.length > 0 && (
        <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar pb-1 at-fade-up" style={{ animationDelay: "110ms" }} data-testid="cuisine-filters">
          <button data-testid="cuisine-filter-all" onClick={() => setCuisineFilter("all")}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium at-press ${cuisineFilter === "all" ? "text-white" : ""}`}
            style={cuisineFilter === "all" ? { background: "var(--at-brand)", borderColor: "var(--at-brand)" } : { borderColor: "var(--at-border)", color: "var(--at-text-2)" }}>
            Toutes cuisines
          </button>
          {availableCuisines.map((id) => {
            const c = CUISINES[id];
            const active = cuisineFilter === id;
            return (
              <button key={id} data-testid={`cuisine-filter-${id}`} onClick={() => setCuisineFilter(active ? "all" : id)}
                className="shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-semibold at-press"
                style={active ? { background: c.color, borderColor: c.color, color: "#fff" } : { background: c.bg, borderColor: "transparent", color: c.color }}>
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      {recipes.length > 0 && (
        <div className="mt-3 flex gap-2 at-fade-up" style={{ animationDelay: "130ms" }}>
          <button data-testid="surprise-btn" onClick={surprise}
            className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-2xl py-3 text-sm font-semibold at-press"
            style={{ background: "var(--at-brand)", color: "#fff" }}>
            <Dices size={18} /> <span className="truncate">Surprends-moi{cuisineFilter !== "all" ? ` · ${(cuisineOf(cuisineFilter) || {}).label || ""}` : ""}</span>
          </button>
          <button data-testid="surprise-quick-btn" onClick={surpriseQuick}
            className="flex shrink-0 items-center justify-center gap-1.5 rounded-2xl border px-3 py-3 text-sm font-semibold at-press"
            style={{ borderColor: "var(--at-honey)", color: "var(--at-honey)" }} title="Un plat au hasard de ≤ 30 min">
            <Zap size={17} /> ≤30 min
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center" style={{ color: "var(--at-muted)" }}>
          <BookOpen size={44} className="mb-3" />
          <p className="font-medium">Aucune recette pour l'instant</p>
          <button onClick={() => navigate("/scan")} className="mt-2 font-semibold underline" style={{ color: "var(--at-brand)" }}>Scanner ma première fiche</button>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {filtered.map((r, i) => <RecipeCard key={r.id} recipe={r} index={i} onToggleFav={toggleFav} currentUserId={user?.user_id} />)}
        </div>
      )}
    </div>
  );
}
